import { useEffect, useState } from "react";
import { Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { NAV, scrollToId, waLink } from "@/lib/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

export const Logo = ({ className = "h-12 w-12" }) => (
  <img src="/brand/tsr-logo-600.jpg" alt="TSR Industry – CNC Wood & Iron Cutting logo" className={`${className} rounded-sm object-cover`} />
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 50);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-[#f6f4ef]/85 backdrop-blur-xl shadow-[0_1px_0_rgba(14,17,22,0.08)] py-2" : "bg-transparent py-4"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between gap-4">
        <button data-testid="header-logo" onClick={() => go("top")} className="flex items-center gap-3" aria-label="TSR Industry home">
          <Logo className="h-11 w-11 sm:h-12 sm:w-12" />
          <span className="hidden sm:flex flex-col leading-none text-left">
            <span className="font-display font-bold text-lg tracking-tight">TSR Industry</span>
            <span className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-slate-600 mt-1">Machilipatnam · AP</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => go(n.id)}
              className="group relative font-medium text-sm text-slate-800 hover:text-black transition-colors">
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold-grad transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a data-testid="header-whatsapp-btn" href={waLink()} target="_blank" rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 h-11 px-4 rounded-full border border-slate-300 bg-white/70 text-sm font-semibold hover:border-[#25D366] hover:text-[#128C7E] transition-colors">
            <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp
          </a>
          <button data-testid="header-quote-btn" onClick={() => go("quote")}
            className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-[#0e1116] text-white text-sm font-semibold hover:bg-black transition-colors">
            Get a Quote <ArrowUpRight className="h-4 w-4 text-[#e8c36a]" />
          </button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="lg:hidden h-11 w-11 grid place-items-center rounded-full border border-slate-300 bg-white/70" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#f6f4ef] w-[86vw] max-w-sm" data-testid="mobile-menu">
              <SheetTitle className="font-display text-left">Menu</SheetTitle>
              <div className="mt-10 flex flex-col gap-1">
                {NAV.map((n, i) => (
                  <button key={n.id} data-testid={`mobile-nav-${n.id}`} onClick={() => go(n.id)}
                    className="flex items-baseline gap-4 py-3 border-b border-slate-300/70 text-left">
                    <span className="font-mono-tech text-xs text-[var(--gold-text)]">0{i + 1}</span>
                    <span className="font-display text-2xl font-semibold">{n.label}</span>
                  </button>
                ))}
                <button data-testid="mobile-nav-quote" onClick={() => go("quote")} className="mt-8 h-12 rounded-full bg-[#0e1116] text-white font-semibold">Request a Quotation</button>
                <a data-testid="mobile-nav-whatsapp" href={waLink()} target="_blank" rel="noopener noreferrer"
                  className="mt-3 h-12 rounded-full bg-[#25D366] text-[#0b2e17] font-semibold inline-flex items-center justify-center gap-2">
                  <WhatsAppIcon /> Chat on WhatsApp
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
